import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { ProactivityExperimentCohortDialog } from './ProactivityExperimentCohortDialog';
import { ProactivityExperimentDialog } from './ProactivityExperimentDialog';

import type {
  ProactivityExperimentDetail,
  ProactivityExperimentOptions,
} from './proactivity-experiments-types';
import type { UseQueryResult } from '@tanstack/react-query';

type ProactivityExperimentDuplicateDialogProps = {
  options: ProactivityExperimentOptions;
  experimentId: string;
  detail: UseQueryResult<ProactivityExperimentDetail>;
  onOpenChange: (open: boolean) => void;
};

export const ProactivityExperimentDuplicateDialog = ({
  options,
  experimentId,
  detail,
  onOpenChange,
}: ProactivityExperimentDuplicateDialogProps) => {
  if (detail.isLoading) {
    return (
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Duplicate proactivity experiment</DialogTitle>
            <DialogDescription>Loading experiment details...</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  if (detail.error) {
    return (
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Duplicate proactivity experiment</DialogTitle>
            <DialogDescription>
              Could not load experiment details.
            </DialogDescription>
          </DialogHeader>
          <p role="alert" className="text-sm text-destructive">
            {detail.error.message}
          </p>
        </DialogContent>
      </Dialog>
    );
  }

  const experiment = detail.data;

  if (!experiment?.prompt) {
    return (
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Duplicate proactivity experiment</DialogTitle>
            <DialogDescription>
              The prompt is missing from the detail response.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <ProactivityExperimentDialog
      key={`duplicate-${experimentId}`}
      mode="duplicate"
      options={options}
      source={experiment}
      onOpenChange={onOpenChange}
    />
  );
};

type ProactivityExperimentCohortDialogLoaderProps = {
  options: ProactivityExperimentOptions;
  detail: UseQueryResult<ProactivityExperimentDetail>;
  onOpenChange: (open: boolean) => void;
};

export const ProactivityExperimentCohortDialogLoader = ({
  options,
  detail,
  onOpenChange,
}: ProactivityExperimentCohortDialogLoaderProps) => {
  if (detail.data) {
    return (
      <ProactivityExperimentCohortDialog
        key={`cohort-${detail.data.id}`}
        experiment={detail.data}
        options={options}
        onOpenChange={onOpenChange}
      />
    );
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit cohort</DialogTitle>
          <DialogDescription>
            {detail.error
              ? 'Could not load the experiment.'
              : 'Loading experiment details...'}
          </DialogDescription>
        </DialogHeader>
        {detail.error ? (
          <p role="alert" className="text-sm text-destructive">
            {detail.error.message}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
